import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import moment from "moment";
import { TranslateService } from "@ngx-translate/core";

import { AppointmentsService } from "../appointments.service";
import { LanguageService } from "../language.service";
import { LoginService } from "../login.service";

@Component({
  selector: "app-transfer-appointments",
  templateUrl: "./transfer-appointments.component.html",
  styleUrls: ["./transfer-appointments.component.scss"],
  standalone: false,
})
export class TransferAppointmentsComponent implements OnInit {
  @Input("sourceDate") sourceDate: Date;
  @Input("appointments") appointments: any[] = [];
  @Input("resourceLabel") resourceLabel: string = "";
  @Input("resourceId") resourceId: string | number = null;

  @Output() closed = new EventEmitter<void>();
  @Output() transferred = new EventEmitter<{ date: Date; resourceId: number | null }>();

  targetDate: moment.Moment = null;
  targetResourceId: number = null;
  selectedIds = new Set<number>();
  minDate = new Date();
  pending = false;
  error = "";

  constructor(
    public appointmentService: AppointmentsService,
    public ls: LoginService,
    private translate: TranslateService,
    private languageService: LanguageService
  ) {}

  get direction(): string {
    return this.languageService.getPageDirection();
  }

  ngOnInit(): void {
    this.selectAll();

    const currentId = this.resourceId ?? (this.byRoom ? this.ls.room : this.ls.doctor);
    this.targetResourceId = currentId != null ? Number(currentId) : null;
  }

  get byRoom(): boolean {
    return !!this.ls.premissions?.ApptsByRoom;
  }

  get resourceOptions(): { id: number; name: string }[] {
    if (this.byRoom) {
      return (this.ls.activeRooms || []).map((room) => ({
        id: Number(room.AutoIndex),
        name: this.ls.roomLabel(room),
      }));
    }
    return (this.ls.clinicUsers || []).map((doctor) => ({
      id: Number(doctor.UID),
      name: doctor.Name,
    }));
  }

  get resourceChanged(): boolean {
    const currentId = this.resourceId ?? (this.byRoom ? this.ls.room : this.ls.doctor);
    return this.targetResourceId != null && Number(this.targetResourceId) !== Number(currentId);
  }

  get sortedAppointments() {
    return [...(this.appointments || [])].sort(
      (a, b) => new Date(a.aptTime).getTime() - new Date(b.aptTime).getTime()
    );
  }

  get allSelected(): boolean {
    return this.appointments?.length > 0 && this.selectedIds.size === this.appointments.length;
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  isSelected(appointment): boolean {
    return this.selectedIds.has(appointment.AutoIndex);
  }

  toggle(appointment) {
    if (this.selectedIds.has(appointment.AutoIndex)) {
      this.selectedIds.delete(appointment.AutoIndex);
    } else {
      this.selectedIds.add(appointment.AutoIndex);
    }
    this.error = "";
  }

  selectAll() {
    this.selectedIds = new Set((this.appointments || []).map((a) => a.AutoIndex));
    this.error = "";
  }

  clearSelection() {
    this.selectedIds.clear();
    this.error = "";
  }

  toggleAll() {
    this.allSelected ? this.clearSelection() : this.selectAll();
  }

  patientName(appointment): string {
    return `${appointment.First || ""} ${appointment.Last || ""}`.trim();
  }

  examName(appointment): string {
    if (appointment.examName) return appointment.examName;
    if (!appointment.Type) return "";

    const key = `appointments.gui.exams.${appointment.Type}`;
    const translated = this.translate.instant(key);
    return translated === key ? appointment.Type : translated;
  }

  appointmentHour(appointment): string {
    return moment(appointment.aptTime).format("HH:mm");
  }

  async confirm() {
    if (this.pending) return;

    if (!this.selectedIds.size) {
      this.error = this.translate.instant("appointments.gui.labels.Select at least one appointment");
      return;
    }

    const target = this.targetDate ? moment(this.targetDate) : moment(this.sourceDate);
    if (target.isSame(moment(this.sourceDate), "day") && !this.resourceChanged) {
      this.error = this.translate.instant("appointments.gui.labels.Nothing to transfer");
      return;
    }

    this.pending = true;
    this.error = "";

    try {
      const targetResource = this.targetResourceId == null
        ? {}
        : this.byRoom
        ? { targetRID: Number(this.targetResourceId) }
        : { targetUID: Number(this.targetResourceId) };

      const result = await this.appointmentService.transferAppointments(
        Array.from(this.selectedIds),
        target.format("YYYY-MM-DD"),
        targetResource
      );

      if (result?.failed?.length) {
        this.error = `${this.translate.instant(
          "appointments.gui.labels.Some appointments were not transferred"
        )} (${result.failed.length})`;
        this.pending = false;
        return;
      }

      this.transferred.emit({
        date: target.toDate(),
        resourceId: this.resourceChanged ? Number(this.targetResourceId) : null,
      });
    } catch (error) {
      this.error =
        error?.error?.error ||
        this.translate.instant("appointments.gui.labels.Transfer appointments failed");
      this.pending = false;
    }
  }

  close() {
    this.closed.emit();
  }
}
